// PERIOD VERSIONING (ADR-0031) calls shared by the admin and seller portals. No
// React here — the apps wrap these in their own react-query hooks.
import { api } from './client';

/**
 * A "Possible new version of …" suggestion (owner decision 2026-10-13): an
 * upload that matched one existing document's category, period and date but was
 * not CLEARLY the same — the AI was not confident how to file it, its file name
 * differs, or it came from the other side of the room (the seller's upload
 * matching the room administrator's document, or the other way round) — so it
 * was kept as its own document and a person decides.
 */
export interface VersionSuggestion {
  id: string;
  /** The newer upload, kept as its own document for now. */
  documentId: string;
  documentName: string | null;
  /** The existing document it may be a new version of. */
  existingDocumentId: string;
  existingDocumentName: string | null;
  /** The AI was not confident about its category, period or date. */
  lowConfidence: boolean;
  /** Its file name does not match the existing document's. */
  nameDiffers: boolean;
  /** One document is the room administrator's and the other the seller's. */
  crossSide: boolean;
  /**
   * Whether the CALLER may merge it or keep it separate. The room administrator
   * always may; the seller only when both documents are the seller's.
   */
  canDecide: boolean;
  createdAt: string;
}

export interface ListVersionSuggestionsResponse {
  suggestions: VersionSuggestion[];
}

export type VersionSuggestionAction = 'merge' | 'keep-separate';

export interface VersionSuggestionActionResponse {
  status: 'merged' | 'kept_separate';
  /** After a merge: the document that now holds both versions. */
  documentId: string;
}

export const versionSuggestionsQueryKey = (roomId: string) =>
  ['rooms', roomId, 'version-suggestions'] as const;

/** The room's open suggestions (the room admin, co-admins and the bound seller). */
export function fetchVersionSuggestions(roomId: string): Promise<VersionSuggestion[]> {
  return api
    .get<ListVersionSuggestionsResponse>(`/rooms/${roomId}/version-suggestions`)
    .then((r) => r.suggestions);
}

/** "Merge as new version" or "Keep separate" on one suggestion. */
export function decideVersionSuggestion(
  roomId: string,
  suggestionId: string,
  action: VersionSuggestionAction,
): Promise<VersionSuggestionActionResponse> {
  return api.post<VersionSuggestionActionResponse>(
    `/rooms/${roomId}/version-suggestions/${suggestionId}/${action}`,
  );
}

/** One version of a document in its version history (ADR-0031). */
export interface DocumentVersion {
  id: string;
  versionNo: number;
  fileName: string | null;
  sizeBytes: number | null;
  /** The uploader as the caller may see it: an email to the room admin, a role label to the seller. */
  uploadedBy: string | null;
  uploadedAt: string;
  isCurrent: boolean;
  /** Came in with a merge and can be split back out (the room admin only). */
  canSplit: boolean;
  /**
   * Every version opens as ITS redacted copy (owner, 2026-10-16) — the same
   * redactions everyone in the data room sees. True while this version has
   * none yet: "Being prepared", nothing to open (the server has queued its
   * redaction). Absent from older servers.
   */
  beingPrepared?: boolean;
}

export interface ListDocumentVersionsResponse {
  versions: DocumentVersion[];
}

/** Under the room's documents key, so anything that refreshes the listing refreshes this too. */
export const documentVersionsQueryKey = (roomId: string, documentId: string) =>
  ['rooms', roomId, 'documents', documentId, 'versions'] as const;

/**
 * How often an open version history re-reads itself while some version's
 * redacted copy is being prepared (so "Being prepared" turns into Open / Download
 * once it is ready); not at all otherwise. For react-query's `refetchInterval`.
 */
export function versionsRefetchInterval(versions: readonly DocumentVersion[] | undefined) {
  return versions?.some((v) => v.beingPrepared) ? 5000 : false;
}

/** A document's versions, newest first (the room admin, co-admins and the bound seller). */
export function fetchDocumentVersions(
  roomId: string,
  documentId: string,
): Promise<DocumentVersion[]> {
  return api
    .get<ListDocumentVersionsResponse>(`/rooms/${roomId}/documents/${documentId}/versions`)
    .then((r) => r.versions);
}

/**
 * The URL that opens (or, with `download`, downloads) one version — its
 * redacted copy, never the original (409 "being prepared" while it has none);
 * the server decides and audits.
 */
export function versionContentUrl(
  roomId: string,
  documentId: string,
  versionId: string,
  download = false,
): string {
  const url = `/api/v1/rooms/${roomId}/documents/${documentId}/versions/${versionId}/content`;
  return download ? `${url}?download=1` : url;
}

/** "Split back out" (the room admin): the version's merge is undone; 202, re-classified. */
export function splitDocumentVersion(
  roomId: string,
  documentId: string,
  versionId: string,
): Promise<{ documentId: string; classificationStatus: string }> {
  return api.post(`/rooms/${roomId}/documents/${documentId}/versions/${versionId}/split`);
}

/** "2.4 MB", "820 KB", "12 bytes"; null when unknown. Pure. */
export function formatFileSize(bytes: number | null): string | null {
  if (bytes === null || !Number.isFinite(bytes) || bytes < 0) return null;
  if (bytes < 1024) return `${bytes} ${bytes === 1 ? 'byte' : 'bytes'}`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}
